import { setMilliseconds, sub } from 'date-fns';
import { createWithEqualityFn as create } from 'zustand/traditional';

import { InsightsBreadcrumb } from 'components/insights/layout/details/helpers/utils';
import { filtersForSelectedGroupType } from 'components/insights/layout/helpers/filters';
import { DATES, END_DATE, START_DATE } from 'constants/filterKeysConstants';
import { calculatePriorPeriod } from 'lib/date';
import FilterValueBoolean from 'models/filterValues/FilterValueBoolean';
import GroupType from 'models/GroupType';
import Profile from 'models/Profile';

import { FilterOption, FilterState } from './types';

const now = setMilliseconds(new Date(), 0);
const thirtyDaysAgo = sub(now, { days: 30 });

export type InsightsFilterState = FilterState<FilterOption[] | string | Date | FilterValueBoolean>;

type Actions = {
  setSelectedGroupType: (groupType: GroupType | undefined) => void;
  setSelectedFilters: (filters: InsightsFilterState) => void;
  setComparePriorPeriod: (bool: boolean) => void;
  setBreadcrumbs: (breadcrumbs: InsightsBreadcrumb[]) => void;
  resetFilters: () => void;
  restoreFiltersFromSnapshot: (snapshotFilters: InsightsFilterState) => void;
  restoreInitialSnapshot: () => void;
  setExpanded: (expanded: boolean) => void;
};

type State = {
  selectedGroupType: GroupType;
  comparePriorPeriod: boolean;
  filters: InsightsFilterState;
  breadcrumbs: InsightsBreadcrumb[];
  actions: Actions;
  expanded: boolean;
};

export const useInsightsStore = create<State>((set) => ({
  selectedGroupType: {} as GroupType,
  comparePriorPeriod: false,
  breadcrumbs: [],
  expanded: false,
  filters: {
    [START_DATE]: thirtyDaysAgo,
    [END_DATE]: now,
  },
  actions: {
    setSelectedGroupType: (groupType) => set({ selectedGroupType: groupType }),
    setComparePriorPeriod: (comparePriorPeriod) => set({ comparePriorPeriod }),
    setBreadcrumbs: (breadcrumbs) => set({ breadcrumbs }),
    setSelectedFilters: (filters) => set((state) => ({ filters: { ...state.filters, ...filters } })),
    resetFilters: () =>
      set({
        filters: {
          [START_DATE]: thirtyDaysAgo,
          [END_DATE]: now,
        },
      }),
    restoreFiltersFromSnapshot: (snapshotFilters: InsightsFilterState) =>
      set(({ filters }) => applySnapshotFilters(filters, snapshotFilters)),
    restoreInitialSnapshot: () =>
      set(({ breadcrumbs, filters }) => {
        if (breadcrumbs.length) {
          return {
            breadcrumbs: [],
            ...applySnapshotFilters(filters, breadcrumbs[0].meta.filters as InsightsFilterState),
          };
        } else {
          return { breadcrumbs, filters };
        }
      }),
    setExpanded: (expanded) => set({ expanded }),
  },
}));
export const useInsightsActions = () => useInsightsStore((state) => state.actions);

export const getAnalyticsFilters = (
  { filters, selectedGroupType, comparePriorPeriod }: State,
  profile: Profile,
  includePriorPeriod = true
) => {
  if (!selectedGroupType) return null;

  const filtersForProviderType: InsightsFilterState = filtersForSelectedGroupType(filters, selectedGroupType, profile);

  const providerKey = selectedGroupType.camelCaseApiName;
  const providerClientKey = `${providerKey}Client`;

  const {
    [START_DATE]: start,
    [END_DATE]: end,
    [providerKey]: group,
    [providerClientKey]: providerClient,
    ...rest
  } = filtersForProviderType;

  return {
    ...rest,
    [DATES]: [
      { start, end },
      ...(comparePriorPeriod && includePriorPeriod ? [calculatePriorPeriod(start as Date, end as Date)] : []),
    ],
    groupType: selectedGroupType.id,
    group,
    providerClient,
  };
};

const applySnapshotFilters = (filters: InsightsFilterState, snapshotFilters: InsightsFilterState) => {
  // persist global date filters that are currently selected
  const { [START_DATE]: start, [END_DATE]: end } = filters;

  return { filters: { ...snapshotFilters, [START_DATE]: start, [END_DATE]: end } };
};
